import { Tooltip2 } from '@blueprintjs/popover2';
import { Icon } from 'antd';
import { getBookingTheme } from '../../components/roster-utilities';
import { IRosterBooking } from '../../types';
import { ROSTER_DAILY_CONFIG } from '../roster-daily-config';
import { getTimelineCardWidth, getTimelineOffset } from './card-width-utils';
import { BookingTypeIcon, StatusTag } from 'common-components/tags';
import { Text } from 'common-components/typography';
import _ from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FaUserTimes } from 'react-icons/fa';
import { BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import { Paper, UnstyledButton, Avatar, ThemeIcon, Group } from '@good/ui/core';
import { useBookings } from 'views/bookings-v2/books-view-context/bookings-view-context';
import { ROSTER_TAB } from '../../components/roster-common-enums';

const { TIMELINE_CARD_HEIGHT, TIMELINE_ROW_HEIGHT } = ROSTER_DAILY_CONFIG;

interface CustomerBookingCardProps {
  booking: IRosterBooking;
  rowIndex: number;
}

export const CustomerBookingCard = ({ booking, rowIndex }: CustomerBookingCardProps) => {
  const { startDateTime, endDateTime, status: bookingStatus, serviceName, scheduleName } = booking;
  const mStartDate = moment(startDateTime);
  const mEndDate = moment(endDateTime);
  const startTimeDisplay = mStartDate.format('h:mm a');
  const endTimeDisplay = mEndDate.format('h:mm a');
  const cardWidth = useMemo(() => getTimelineCardWidth(startDateTime, endDateTime), [startDateTime, endDateTime]);
  const topOffset = rowIndex === 0 ? 4 : rowIndex * TIMELINE_ROW_HEIGHT + 4;
  const leftOffset = useMemo(() => getTimelineOffset(startDateTime), [startDateTime]);
  const { bgColor, borderColor } = getBookingTheme(bookingStatus);
  const onSelectRosterItem = useBookings((selector) => selector.onSelectRosterItem);
  const selectedRosterItem = useBookings((selector) => selector.selectedRosterItem);
  const isSelected = selectedRosterItem?.bookingId === booking.bookingId;

  const isCancelled = !!_.find(
    [
      BookingStatus.BUSINESS_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED_WITH_FEE,
      BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE,
    ],
    (status) => status === booking.status,
  );

  const name = `${booking.workerFirstName ?? ''} ${booking.workerLastName ?? ''}`;

  return (
    <>
      <UnstyledButton
        onClick={() => onSelectRosterItem({ item: booking, type: 'customerBooking', tab: ROSTER_TAB.DAILY })}
        display='flex'
        w={cardWidth}
        top={topOffset}
        left={leftOffset}
        h={TIMELINE_CARD_HEIGHT}
        pos='absolute'
        style={{
          zIndex: 10,
          boxShadow: isSelected ? '0 0 0 2px var(--mantine-color-lavender-6)' : '',
          borderRadius: 'calc(var(--mantine-radius-md) - 2px)',
        }}
      >
        <Paper
          p={6}
          w={cardWidth}
          withBorder
          h={TIMELINE_CARD_HEIGHT}
          shadow={isSelected ? 'xl' : 'xs'}
          bg={bgColor}
          style={{
            borderColor: borderColor,
            overflow: 'hidden',
          }}
          className='anim-fade-in'
        >
          <div className={'flex-row justify-start align-center '}>
            <StatusTag status={bookingStatus} size={'small'} isFixedWidth={true} width={cardWidth - 24} />
            {booking.serviceType === ServiceType.INDIVIDUAL && (
              <div className={'ml-small'}>
                <Tooltip2
                  content={
                    <Text color={'white'}>
                      {booking.workerId
                        ? name
                        : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING
                          ? 'Published'
                          : 'No team member assigned'}
                    </Text>
                  }
                  position={'bottom-right'}
                >
                  <Group gap={2} align='center'>
                    {booking.workerId ? (
                      <>
                        <Avatar size={20} name={name} color='initials' />
                        <ThemeIcon
                          variant='light'
                          radius='50%'
                          size={20}
                          color={
                            booking.shiftSlotStatus === ShiftSlotStatus.PENDING
                              ? 'var(--mantine-color-orange-4)'
                              : 'var(--mantine-color-ocean-3)'
                          }
                        >
                          <Icon
                            type={booking.shiftSlotStatus === ShiftSlotStatus.PENDING ? 'clock-circle' : 'check-circle'}
                          />
                        </ThemeIcon>
                      </>
                    ) : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ? (
                      <Icon type='wifi' className='text-color-violet-light mr-x-small text-size-regular' />
                    ) : !isCancelled ? (
                      <FaUserTimes className={'text-color-red text-size-large'} />
                    ) : (
                      <></>
                    )}
                  </Group>
                </Tooltip2>
              </div>
            )}
          </div>
          <div className='pt-x2-small flex-column line-height-100'>
            <div className='text-overflow-ellipsis overflow-hidden whitespace-nowrap'>
              <Text lineHeight={100} size='small' className='whitespace-nowrap'>
                <b>{startTimeDisplay}</b> - {endTimeDisplay}
              </Text>
            </div>
            <div className={'flex-row align-center'}>
              <BookingTypeIcon
                type={
                  booking.bookingType === BookingType.ACTIVITY_RECORD
                    ? BookingType.ACTIVITY_RECORD
                    : booking.serviceType
                }
                className={'mr-x-small'}
              />
              <div className='text-overflow-ellipsis overflow-hidden whitespace-nowrap flex-row align-center'>
                <Text lineHeight={100} size='regular' className='whitespace-nowrap'>
                  {booking.serviceType === ServiceType.GROUP && scheduleName ? scheduleName : serviceName}
                </Text>
              </div>
            </div>
          </div>
        </Paper>
      </UnstyledButton>
    </>
  );
};
